import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import {
  CompetitorsSob,
  SobCompetitorsWarning,
} from 'legacy/lut/components/widgets/public/partner360/competitorsSob'
import { CompetitorsSobComment } from 'legacy/lut/components/widgets/public/partner360/competitorsSob/components/competitorsSobComment/CompetitorsSobComment'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { LoaderOverlay } from 'cng-common/components/loaderOverlay'
import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { NextButton } from 'partner360/components/QBR/components/qbrDocumentDetails/components/nextButton/NextButton'
import { QBRErrorModal } from 'partner360/components/QBR/components/qbrDocumentDetails/components/steps/settingsStep/components/qbrErrorModal/QBRErrorModal'
import { useRefreshing } from 'partner360/components/QBR/components/qbrDocumentDetails/hooks/useRefreshing'
import { TRANSLATION_KEYS } from 'partner360/components/QBR/translations'
import {
  changeIsSelectedDocumentChanged,
  collapseQBRSobCompetitorsTableRow,
  expandQBRSobCompetitorsTableRow,
  selectQbrCorePrintSobCompetitorsDependOnSettings,
  selectQbrCurrency,
  selectQbrNetNdp,
  selectQbrPCSobCompetitorsDependOnSettings,
  selectQbrPersonalSystemsSobCompetitorsDependOnSettings,
  selectQbrPrintSobCompetitorsDependOnSettings,
  selectQBRSobCompetitorsComments,
} from 'partner360/features/QBR'
import { selectQbrSelectedPeriod } from 'partner360/features/QBR/selectors/selectQbrSelectedPeriod'

import { useQbrSave } from '../../../hooks/useQbrSave'

import styles from './QbrSobCompetitors.module.scss'

export const QbrSobCompetitors = () => {
  const dataPrint = useSelector(selectQbrPrintSobCompetitorsDependOnSettings)
  const dataPC = useSelector(selectQbrPCSobCompetitorsDependOnSettings)
  const dataCorePrint = useSelector(selectQbrCorePrintSobCompetitorsDependOnSettings)
  const dataPersonalSystems = useSelector(selectQbrPersonalSystemsSobCompetitorsDependOnSettings)
  const selectedPeriod = useSelector(selectQbrSelectedPeriod)
  const netNdp = useSelector(selectQbrNetNdp)
  const currency = useSelector(selectQbrCurrency)
  const comments = useSelector(selectQBRSobCompetitorsComments)

  const { refresh, isRefreshingProgress } = useRefreshing()

  const selectedPeriodFYear = useMemo(() => selectedPeriod.fYear, [selectedPeriod.fYear])

  const { isSavingProgress, onNextButtonClick, showErrorModal, onCloseErrorModal } = useQbrSave()

  const dispatch = useAppDispatch()

  const selectedPeriodFQuarters = useMemo(
    () => selectedPeriod.fQuarters.map(({ fQuarter }) => fQuarter),
    [selectedPeriod.fQuarters]
  )

  const onCollapse = useCallback(
    (tableType: ProgramTypes) =>
      (keyField: string, keyFieldValue: string | number, hierarchyField: string) => {
        dispatch(
          collapseQBRSobCompetitorsTableRow({ keyField, keyFieldValue, hierarchyField, tableType })
        )
        dispatch(changeIsSelectedDocumentChanged(true))
      },
    [dispatch]
  )

  const onExpand = useCallback(
    (tableType: ProgramTypes) =>
      (keyField: string, keyFieldValue: string | number, hierarchyField: string) => {
        dispatch(
          expandQBRSobCompetitorsTableRow({ keyField, keyFieldValue, hierarchyField, tableType })
        )
        dispatch(changeIsSelectedDocumentChanged(true))
      },
    [dispatch]
  )

  if (!dataPC.length && !dataPrint.length && !dataCorePrint.length && !dataPersonalSystems.length) {
    return (
      <div className={styles.noData}>
        <LoaderOverlay isVisible={isRefreshingProgress} />
        <p>
          <Translation text={TRANSLATION_KEYS.refreshDocumentToFetchCompetitors} />
        </p>
        <Button className={styles.button} onClick={refresh} disabled={isRefreshingProgress}>
          <Translation text={TRANSLATION_KEYS.refreshDocument} />
        </Button>
      </div>
    )
  }

  return (
    <div>
      <SobCompetitorsWarning />
      <CompetitorsSob
        fYear={selectedPeriodFYear}
        netNdp={netNdp}
        currency={currency}
        className={styles.table}
        data={dataPrint}
        title={ProgramTypes.Print}
        onExpand={onExpand(ProgramTypes.Print)}
        onCollapse={onCollapse(ProgramTypes.Print)}
        selectedPeriodFQuarters={selectedPeriodFQuarters}
        tableBanner={
          <CompetitorsSobComment
            showIfOnlyExists
            className={styles.commentBanner}
            value={comments[ProgramTypes.Print]}
          />
        }
      />
      <CompetitorsSob
        fYear={selectedPeriodFYear}
        netNdp={netNdp}
        currency={currency}
        className={styles.table}
        data={dataPC}
        title={ProgramTypes.PC}
        onExpand={onExpand(ProgramTypes.PC)}
        onCollapse={onCollapse(ProgramTypes.PC)}
        selectedPeriodFQuarters={selectedPeriodFQuarters}
        tableBanner={
          <CompetitorsSobComment
            showIfOnlyExists
            className={styles.commentBanner}
            value={comments[ProgramTypes.PC]}
          />
        }
      />
      <CompetitorsSob
        fYear={selectedPeriodFYear}
        netNdp={netNdp}
        currency={currency}
        className={styles.table}
        data={dataCorePrint}
        title={ProgramTypes.CorePrint}
        onExpand={onExpand(ProgramTypes.CorePrint)}
        onCollapse={onCollapse(ProgramTypes.CorePrint)}
        selectedPeriodFQuarters={selectedPeriodFQuarters}
        tableBanner={
          <CompetitorsSobComment
            showIfOnlyExists
            className={styles.commentBanner}
            value={comments[ProgramTypes.CorePrint]}
          />
        }
      />
      <CompetitorsSob
        fYear={selectedPeriodFYear}
        netNdp={netNdp}
        currency={currency}
        className={styles.table}
        data={dataPersonalSystems}
        title={ProgramTypes.PersonalSystems}
        onExpand={onExpand(ProgramTypes.PersonalSystems)}
        onCollapse={onCollapse(ProgramTypes.PersonalSystems)}
        selectedPeriodFQuarters={selectedPeriodFQuarters}
        tableBanner={
          <CompetitorsSobComment
            showIfOnlyExists
            className={styles.commentBanner}
            value={comments[ProgramTypes.PersonalSystems]}
          />
        }
      />

      <NextButton onClick={onNextButtonClick} />
      <QBRErrorModal
        show={showErrorModal}
        onCloseButtonClick={onCloseErrorModal}
        isButtonDisabled={isSavingProgress}
      />
    </div>
  )
}
